import { Children, forwardRef, isValidElement, memo, useEffect } from 'react';
import type { ComponentPropsWithoutRef } from 'react';

import { cx } from '@/utils/cx';
import { HeaderLogoWidget } from '@/widgets/HeaderLogoWidget';

import styles from './HeaderLeft.module.scss';

import type { VisibleOn } from '../types';

export interface HeaderLeftProps extends ComponentPropsWithoutRef<'div'> {
  /** 특정 디바이스에서만 노출. 미지정 = 항상 노출 */
  visibleOn?: VisibleOn;
}

/** dev 전용: Logo 좌/우 sibling 수 검증 */
function validateChildren(children: HeaderLeftProps['children']) {
  if (process.env.NODE_ENV === 'production') return;

  const childArray = Children.toArray(children).filter(isValidElement);

  // Logo는 직접 참조 비교로 식별 — displayName 문자열 비교는 minification mangling 위험 및
  // displayName 미선언 시 일반 HTML element 와 false-match 위험이 있어 사용하지 않는다.
  // Header.Logo dot-notation 은 widget-backed (HeaderLogoWidget) 라 widget reference 로 비교.
  const logoIndex = childArray.findIndex((child) => child.type === HeaderLogoWidget);

  // Logo는 선택 사항. 없어도 렌더링 정상 동작.
  if (logoIndex === -1) return;

  const beforeLogo = childArray.slice(0, logoIndex);
  const afterLogo = childArray.slice(logoIndex + 1);

  if (beforeLogo.length > 1) {
    // eslint-disable-next-line no-console
    console.warn(`[Header.Left] 로고 좌측에 ${beforeLogo.length}개 요소가 있습니다. 최대 1개를 권장합니다.`);
  }

  if (afterLogo.length > 1) {
    // eslint-disable-next-line no-console
    console.warn(`[Header.Left] 로고 우측에 ${afterLogo.length}개 요소가 있습니다. 최대 1개를 권장합니다.`);
  }
}

export const HeaderLeft = memo(
  forwardRef<HTMLDivElement, HeaderLeftProps>(({ children, className, visibleOn, ...rest }, ref) => {
    useEffect(() => {
      validateChildren(children);
    }, [children]);

    return (
      <div
        className={cx(styles.left, className)}
        data-core-ui-header-slot="left"
        data-core-ui-visible-on={visibleOn}
        ref={ref}
        {...rest}
      >
        {children}
      </div>
    );
  }),
);

HeaderLeft.displayName = 'Header.Left';
